import { IonButton, useIonAlert } from '@ionic/react';
import i18next from 'i18next';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useProduct } from '../../api/hooks/products/useProduct';
import { useProductForm } from '../../api/hooks/products/useProductForm';
import { useDeleteProduct } from '../../api/hooks/products/useUpdateCreateProduct';
import { Loading } from '../../components/atoms/Loading';
import { ViewField } from '../../components/atoms/ViewField';
import { PATHS } from '../../navigation/data';
import { useHasActivePlan } from '../../services/utils/useHasActivePlan';
import { ProductInvoices } from './components/ProductInvoices';

export const ProductScreen = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasActivePlan = useHasActivePlan();
  const { data: product, isLoading } = useProduct(id);
  const { data: formData, isLoading: isLoadingForm } = useProductForm();
  const {
    mutate: deleteEntity,
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSuccess,
    data: deleteResp,
  } = useDeleteProduct();
  const [presentAlert] = useIonAlert();

  useEffect(() => {
    if (isDeleteSuccess && deleteResp?.success) {
      navigate(PATHS.PRODUCTS, { replace: true });
    }
  }, [deleteResp?.success, isDeleteSuccess, navigate]);

  const navigateToEdit = useCallback(() => {
    navigate(`${PATHS.PRODUCTS}/${id}`);
  }, [navigate, id]);

  const deleteConfirmation = useCallback(() => {
    presentAlert({
      mode: 'ios',
      header: t('FORMS.CONFIRM_DELETE_MSG'),
      buttons: [
        { text: t('SHARED.CANCEL'), role: 'cancel', cssClass: 'noTextTransformation' },
        {
          text: t('SHARED.CONFIRM'),
          role: 'confirm',
          handler: () => {
            // @ts-expect-error ts error from lib
            deleteEntity({ id });
          },
          cssClass: 'noTextTransformation',
        },
      ],
    });
  }, [presentAlert, t, id]);

  const categoryName = formData?.productCategories.find((c) => c.id == product?.productCategoryId)?.name;
  const unitName = formData?.units.find((u) => u.id == product?.unitId)?.name;
  const vatName = formData?.vatPercentages.find((v) => v.id == product?.vatPercentageId)?.name;
  const saleCategoryData = formData?.saleCategories.find((c) => c.id == product?.saleCategoryId);
  const saleCharacterizationData = formData?.saleCharacterizations.find((c) => c.id == product?.saleCharacterizationId);

  return (
    <div className="width-100 flex-center-center-column-nowrap padding-b-60">
      {(isLoading || isLoadingForm || isDeleteLoading) && <Loading backgroundFull />}
      <div className="page flex-center-start-column-nowrap">
        <div className="padding-b-10 flex-space-between-start-row-wrap width-100">
          <div className="flex-start-center-row-wrap">
            {hasActivePlan && (
              <IonButton mode="md" onClick={navigateToEdit}>
                {t('FORMS.EDIT')}
              </IonButton>
            )}
          </div>
          {hasActivePlan && product?.canDelete && (
            <IonButton mode="md" onClick={deleteConfirmation} fill="outline" className="ion-delete-button">
              {t('FORMS.DELETE')}
            </IonButton>
          )}
        </div>
        {product && (
          <div className="docForm margin-t-12 margin-b-10">
            <div className="padding-t-24 padding-b-24 padding-l-8 padding-r-8 flex-start-center-row-wrap gap-30-16">
              <ViewField label="FORMS.NAME" value={product.name} />
              {product.internalId && <ViewField label="FORMS.INTERNAL_ID" value={product.internalId} />}
              {product.description && <ViewField label="FORMS.DESCRIPTION" value={product.description} />}
              {categoryName && (
                <ViewField label="FORMS.PRODUCT_CATEGORY" value={i18next.t(`PRODUCT_CATEGORIES.${categoryName}`)} />
              )}
              {unitName && <ViewField label="FORMS.MEASUREMENT_UNIT" value={i18next.t(`PRODUCT_UNITS.${unitName}`)} />}
              {vatName && <ViewField label="FORMS.VAT_PERCENTAGE" value={i18next.t(`VAT_REGIME.${vatName}`)} />}
              {saleCategoryData && (
                <ViewField
                  label="FORMS.SALE_CATEGORY"
                  value={`${saleCategoryData.aadeCode}: ${i18next.t(`CHARACTERIZATIONS.${saleCategoryData.name}`)}`}
                />
              )}
              {saleCharacterizationData && (
                <ViewField
                  label="FORMS.SALE_CHARACTERIZATION"
                  value={`${saleCharacterizationData.aadeCode}: ${i18next.t(`CHARACTERIZATIONS.${saleCharacterizationData.name}`)}`}
                />
              )}
              <ViewField
                label="FORMS.VAT_DISCOUNT_ELIGIBILITY"
                value={product.validForTaxDeduction ? t('SHARED.YES') : t('SHARED.NO')}
              />
            </div>
          </div>
        )}
        {id && <ProductInvoices productId={id} />}
      </div>
    </div>
  );
};
